/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
 the European Commission - subsequent versions of the EUPL (the "Licence");
 You may not use this work except in compliance with the Licence.
 You may obtain a copy of the Licence at:

   https://joinup.ec.europa.eu/software/page/eupl

 Unless required by applicable law or agreed to in writing, software
 distributed under the Licence is distributed on an "AS IS" basis,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the Licence for the specific language governing permissions and
 limitations under the Licence. */

import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import { Box, Button, Divider, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { GroupOfStopPlacesActionsProps } from "../types";

/**
 * Action buttons component for group of stop places
 * Matches EditStopPage footer pattern: Terminate left, Undo+Save right
 */
export const GroupOfStopPlacesActions: React.FC<
  GroupOfStopPlacesActionsProps
> = ({
  hasId,
  isModified,
  canEdit,
  canDelete,
  hasName,
  hasMembers,
  onRemove,
  onUndo,
  onSave,
}) => {
  const { formatMessage } = useIntl();

  const isSaveDisabled = !isModified || !hasName || !hasMembers || !canEdit;
  const isUndoDisabled = !isModified || !canEdit;

  return (
    <>
      <Divider />
      <Box
        sx={{
          display: "flex",
          gap: 1,
          px: 2,
          py: 1.5,
          bgcolor: "background.paper",
          flexWrap: "wrap",
        }}
      >
        {hasId && canDelete && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={onRemove}
          >
            {formatMessage({ id: "remove" })}
          </Button>
        )}
        {canEdit && !hasMembers && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ width: "100%", order: -1 }}
          >
            {formatMessage({ id: "group_requires_members" })}
          </Typography>
        )}
        {canEdit && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UndoIcon />}
              onClick={onUndo}
              disabled={isUndoDisabled}
              sx={{ ml: "auto" }}
            >
              {formatMessage({ id: "undo_changes" })}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SaveIcon />}
              onClick={onSave}
              disabled={isSaveDisabled}
            >
              {formatMessage({ id: "save" })}
            </Button>
          </>
        )}
      </Box>
    </>
  );
};
